'use client';
import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useScrollToCurrentSelection } from 'hooks/useScrollToCurrentSelection';
import type { PropsWithClassName } from 'types/react-props';
import type { VideoProps } from 'types/videoTypes';
import { generateWeakKey } from 'utils/idGenerator';
import { formatSecondsToMinutes } from 'utils/time';
import { getVideoDuration } from 'utils/videoPlayer';
import { VideoThumbnail } from '../VideoThumbnail';
import NavButtons from './components/NavButtons';
import styles from './VideoSwiper.module.scss';

type VideoSwiperProps = PropsWithClassName<{
  readonly videoList: Array<VideoProps>;
  readonly currentVideoIndex?: number;
  readonly onClick?: (currentVideoIndex: number) => void;
  readonly isAdPlaying?: boolean;
  readonly adClipsLeft?: number;
  readonly videoPlaying?: boolean;
  readonly initHidden?: boolean;
  readonly showRelatedVideos?: boolean;
  readonly bgColor?: string;
}>;

const VideoSwiper = ({
  videoList,
  currentVideoIndex,
  onClick,
  isAdPlaying = false,
  adClipsLeft = 0,
  videoPlaying = false,
  initHidden = false,
  showRelatedVideos = false,
  className,
  bgColor = '#000000',
}: VideoSwiperProps) => {
  const videosCount = videoList.length;

  const [scrollIndex, setScrollIndex] = useState(currentVideoIndex ?? 0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollRef = useScrollToCurrentSelection(scrollIndex);

  if (currentVideoIndex !== undefined && currentVideoIndex !== scrollIndex) {
    setScrollIndex(currentVideoIndex);
  }

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const updateScrollButtons = () => {
      if (!scrollRef.current) return;

      const container = scrollRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    updateScrollButtons();

    container.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [scrollRef]);

  if (!videosCount) return null;

  const switchCurrentVideo = (index: number) => {
    setScrollIndex(index);
    onClick?.(index);
  };

  const scrollByPercentage = (direction: 'left' | 'right', percentage: number) => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const scrollAmount = container.offsetWidth * (percentage / 100);
    const scrollOptions: ScrollToOptions = {
      left: direction === 'right' ? container.scrollLeft + scrollAmount : container.scrollLeft - scrollAmount,
      behavior: 'smooth',
    };

    container.scrollTo(scrollOptions);
  };

  const scrollRight = () => scrollByPercentage('right', 25);
  const scrollLeft = () => scrollByPercentage('left', 25);

  return (
    <div
      className={clsx(styles.root, className, {
        [styles.hidden]: initHidden,
        [styles.animationOnShow]: showRelatedVideos,
      })}
      data-testid="video-swiper"
      aria-label="Playlist Swiper"
    >
      {isAdPlaying && (
        <div className={styles.thumbnailOverlay}>
          <span className={styles.adPlayingOverlayText}>
            {adClipsLeft > 1 ? `Nach ${adClipsLeft} Spots geht es weiter` : 'Nur ein Spot'}
          </span>
        </div>
      )}
      <div className={clsx(styles.swiperThumbnails, isAdPlaying ? styles.disableSwiper : null)}>
        <div className={styles.swiperContainer}>
          <ul className={clsx(styles.scrollContainer)} ref={scrollRef} aria-live="polite">
            {videoList.map((video: VideoProps, index: number) => {
              return (
                <li key={generateWeakKey(video)} className={styles.thumbnailWrapper}>
                  <VideoThumbnail
                    id={video.teaserId}
                    isActive={currentVideoIndex === index && videoPlaying}
                    title={video.fields.title}
                    kicker={video.fields.subtitle ?? ''}
                    duration={formatSecondsToMinutes(getVideoDuration(video))}
                    video={video}
                    isAdPlaying={isAdPlaying}
                    onClick={() => {
                      switchCurrentVideo(index);
                    }}
                    aria-current={currentVideoIndex === index}
                  />
                </li>
              );
            })}
          </ul>
          <NavButtons
            className={styles.navButtons}
            scrollLeft={scrollLeft}
            scrollRight={scrollRight}
            showLeft={canScrollLeft}
            showRight={canScrollRight}
            bgColor={bgColor}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoSwiper;
